import type { SingleSelectDropdownOption } from "@carbon6/c6-frontend-design";

/**
 * cron 시간대 선택지. 브라우저가 아는 모든 IANA 시간대(`Intl.supportedValuesOf`)에 현재 UTC
 * 오프셋을 붙인다. 목록을 코드에 두지 않으므로 새 시간대·별칭이 생겨도 코드를 고칠 필요가 없다.
 *
 * `supportedValuesOf` 가 없는 브라우저(Chrome 99·Safari 15.4·Firefox 93 이전)에서는 빈 목록을
 * 반환하고, 호출부는 그때 텍스트 입력을 쓴다.
 */

// TS lib 이 ES2020 이라 선언이 없다. 함수가 있는지 확인한 뒤 호출한다.
type IntlWithSupportedValues = typeof Intl & {
  supportedValuesOf?: (key: "timeZone") => string[];
};

let cachedNames: readonly string[] | null = null;

function supportedTimeZoneNames(): readonly string[] {
  if (cachedNames !== null) {
    return cachedNames;
  }
  const intl = Intl as IntlWithSupportedValues;
  let names: string[] = [];
  try {
    names = intl.supportedValuesOf?.("timeZone") ?? [];
  } catch {
    names = [];
  }
  // 엔진에 따라 "UTC" 가 빠진다. 상류 기본값이라 항상 고를 수 있어야 한다.
  if (names.length > 0 && !names.includes("UTC")) {
    names = [...names, "UTC"];
  }
  cachedNames = names;
  return names;
}

// "GMT+09:00" → "UTC+09:00". 오프셋 0 은 "GMT" 한 단어로 오므로 "+00:00" 을 채운다.
export function timeZoneOffsetLabel(name: string, at: Date = new Date()): string {
  try {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: name,
      // ES2020 lib 타입에는 없는 값이라 타입을 넓혀 전달한다.
      timeZoneName: "longOffset" as Intl.DateTimeFormatOptions["timeZoneName"],
    }).formatToParts(at);
    const raw = parts.find((part) => part.type === "timeZoneName")?.value ?? "";
    const offset = raw.replace(/^GMT/, "");
    return `UTC${offset === "" ? "+00:00" : offset}`;
  } catch {
    return "";
  }
}

function toOption(name: string): SingleSelectDropdownOption {
  const offset = timeZoneOffsetLabel(name);
  return { value: name, label: offset === "" ? name : `${name} (${offset})` };
}

/**
 * 드롭다운 옵션. 저장된 값이 목록에 없으면(구 별칭 등) 맨 앞에 추가해서 선택이 비지 않게 한다.
 * 브라우저가 목록을 주지 않으면 빈 배열이다.
 */
export function listTimeZoneOptions(current: string): SingleSelectDropdownOption[] {
  const names = supportedTimeZoneNames();
  if (names.length === 0) {
    return [];
  }
  const trimmed = current.trim();
  const options = baseTimeZoneOptions(names);
  if (trimmed !== "" && !names.includes(trimmed)) {
    return [toOption(trimmed), ...options];
  }
  return options;
}

// 400여 개 이름마다 Intl.DateTimeFormat 을 만드는 값이라 한 번만 만든다. 오프셋 라벨은 세션
// 안에서 바뀌지 않는다(DST 경계는 다음 로드에 반영된다).
let cachedOptions: SingleSelectDropdownOption[] | null = null;

function baseTimeZoneOptions(names: readonly string[]): SingleSelectDropdownOption[] {
  if (cachedOptions === null) {
    cachedOptions = names.map(toOption);
  }
  return cachedOptions;
}
